import SectionLabel from "@/components/base/SectionLabel";

interface CaseDetailData {
  label: string;
  subLabel: string;
  title: string;
  orgType: string;
  orgName?: string;
  orgNameAnonymous?: string;
  isAnonymous?: boolean;
  beforeTitle: string;
  beforeDescription: string;
  challenges: string[];
  supportTitle: string;
  supportDescription: string;
  supportItems: string[];
  afterTitle: string;
  afterDescription?: string;
  afterStats: { label: string; value: string }[];
  flowImage?: string;
  beforeFlow?: {
    title: string;
    steps: string[];
  };
  afterFlow?: {
    title: string;
    steps: string[];
  };
}

interface CaseDetailSectionProps {
  caseData: CaseDetailData;
  sectionId: string;
}

export default function CaseDetailSection({ caseData, sectionId }: CaseDetailSectionProps) {
  return (
    <section id={sectionId} className="pt-14 md:pt-20 pb-8 md:pb-12">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 md:mb-14 reveal">
          <SectionLabel
            label={`${caseData.label}  ${caseData.subLabel}`}
            className="mb-3"
          />
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 md:mb-5">
            {caseData.title}
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-block text-xs md:text-sm font-medium text-foreground-700 font-label">
              {caseData.orgType}
            </span>
            {caseData.orgName && (
              <span className="inline-block text-xs md:text-sm font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                {caseData.isAnonymous ? caseData.orgNameAnonymous : caseData.orgName}
              </span>
            )}
          </div>
        </div>

        {/* Before */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-16 reveal">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-200 text-foreground-700 flex items-center justify-center text-sm font-bold font-label flex-shrink-0">
              前
            </span>
            {caseData.beforeTitle}
          </h3>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 md:mb-8 pl-0 md:pl-11">
            {caseData.beforeDescription}
          </p>

          <div className="pl-0 md:pl-11">
            <h4 className="font-label text-xs md:text-sm font-bold text-foreground-800 mb-3 md:mb-4 uppercase tracking-wider">
              見えてきた課題
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
              {caseData.challenges.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2.5 p-3 rounded-md bg-background-100/70 border border-background-200/50"
                >
                  <i className="ri-error-warning-line text-accent-500 flex-shrink-0 mt-px" />
                  <span className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-16 reveal">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary-500/10 text-primary-600 flex items-center justify-center text-sm font-bold font-label flex-shrink-0">
              支
            </span>
            {caseData.supportTitle}
          </h3>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 md:mb-8 pl-0 md:pl-11">
            {caseData.supportDescription}
          </p>

          <div className="pl-0 md:pl-11">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
              {caseData.supportItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2.5 md:p-3 rounded-md bg-primary-50/60 border border-primary-100/50"
                >
                  <i className="ri-check-line text-primary-500 flex-shrink-0 text-sm" />
                  <span className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* After */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-16 reveal">
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-accent-500/10 text-accent-600 flex items-center justify-center text-sm font-bold font-label flex-shrink-0">
              後
            </span>
            {caseData.afterTitle}
          </h3>
          {caseData.afterDescription && (
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 pl-0 md:pl-11">
              {caseData.afterDescription}
            </p>
          )}

          <div className="pl-0 md:pl-11">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {caseData.afterStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="p-3 md:p-4 rounded-md bg-background-50 border border-background-200/70 text-center"
                >
                  <p className="text-[10px] md:text-xs text-foreground-500 font-label mb-1">
                    {stat.label}
                  </p>
                  <p className="text-sm md:text-base font-bold text-foreground-900 leading-snug">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flow Diagram or Before/After Comparison */}
        <div className="max-w-3xl mx-auto mb-10 md:mb-14 reveal">
          {caseData.flowImage ? (
            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <img
                src={caseData.flowImage}
                alt="支援の流れ"
                className="w-full h-auto object-cover"
              />
            </div>
          ) : caseData.beforeFlow && caseData.afterFlow ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Before */}
              <div className="rounded-lg border border-background-200/70 overflow-hidden">
                <div className="bg-background-100/80 px-4 py-3 border-b border-background-200/70">
                  <h4 className="font-label text-xs font-bold text-foreground-800 uppercase">
                    {caseData.beforeFlow.title}
                  </h4>
                </div>
                <div className="p-4 md:p-5 space-y-3">
                  {caseData.beforeFlow.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full bg-background-200 border border-background-300/60 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-foreground-600 font-label">
                            {idx + 1}
                          </span>
                        </div>
                        {idx < caseData.beforeFlow!.steps.length - 1 && (
                          <div className="w-px h-5 bg-background-300/60" />
                        )}
                      </div>
                      <span className="text-xs md:text-sm text-foreground-700 py-0.5">
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* After */}
              <div className="rounded-lg border border-primary-200/60 overflow-hidden">
                <div className="bg-primary-50/80 px-4 py-3 border-b border-primary-200/40">
                  <h4 className="font-label text-xs font-bold text-primary-700 uppercase">
                    {caseData.afterFlow.title}
                  </h4>
                </div>
                <div className="p-4 md:p-5 space-y-3">
                  {caseData.afterFlow.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-6 h-6 rounded-full bg-primary-500/10 border border-primary-200/50 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-primary-600 font-label">
                            {idx + 1}
                          </span>
                        </div>
                        {idx < caseData.afterFlow!.steps.length - 1 && (
                          <div className="w-px h-5 bg-primary-200/50" />
                        )}
                      </div>
                      <span className="text-xs md:text-sm text-foreground-700 py-0.5">
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}